import React from 'react';
import { groupBy, uniq, flatten, sumBy, isArray } from 'lodash';
import PieChart from '../../../../_Charts/BreakdownCharts/PieChart/PieChart';
import NoDataMessage from '../../../../_Components/NoDataMessage/NoDataMessage';
import TreemapChart from '../../../../_Charts/BreakdownCharts/TreemapChart/TreemapChart';
import RiverChart from '../../../../_Charts/BreakdownCharts/PieChart/RiverChart/RiverChart';

export const flat = (array: any) => {
  let result: Array<any> = [];
  array.forEach((a: any) => {
    result.push(a);
    if (Array.isArray(a.subThematics || a.subSubThematics || a.children)) {
      result = result.concat(flat(a.subThematics || a.subSubThematics || a.children));
    }
  });
  return result;
};

export const getFlattenedData = (dataToFlatten: any[]) => {
  const flatData = flat(dataToFlatten)
    .map(d => {
      if (d.subThematics) {
        return d;
      }
      if (typeof d.subSubThematics !== 'undefined') {
        return {
          ...d,
          thematic: dataToFlatten.find((them: { subThematics: any[] }) =>
            them.subThematics.find(
              (sub: { thematicsOverviewDetails: any }) => sub?.thematicsOverviewDetails === d?.thematicsOverviewDetails,
            ),
          )?.thematicsOverviewDetails?.name,
        };
      }
      if (!d.subThematics && !d.subSubThematics) {
        const thematic = dataToFlatten.find((them: { subThematics: any[] }) =>
          them.subThematics?.find((sub: { subSubThematics: any[] }) =>
            sub.subSubThematics?.find(
              (subsub: { thematicsOverviewDetails: any }) =>
                subsub?.thematicsOverviewDetails === d?.thematicsOverviewDetails,
            ),
          ),
        );
        return {
          ...d,
          thematic: thematic?.thematicsOverviewDetails?.name,
          subThematic: thematic?.subThematics?.find((sub: { subSubThematics: any[] }) =>
            sub?.subSubThematics?.find(
              (subsub: { thematicsOverviewDetails: any }) =>
                subsub?.thematicsOverviewDetails === d?.thematicsOverviewDetails,
            ),
          )?.thematicsOverviewDetails?.name,
        };
      }
    })
    .filter(d => d.thematicsOverviewDetails && d.thematicsOverviewDetails.isSelected);
  return flatData;
};

let formattedData: any[] = [];

export const formatDeep = (arrayToIterate: any[], level: string | undefined, objToFill: {}) => {
  let newObjToFill = {};
  arrayToIterate?.map((item: { [x: string]: any; thematicsOverviewDetails?: any }) => {
    const childrenKey = Object.keys(item).find(i => Array.isArray(item[i]));
    newObjToFill = {
      ...objToFill,
      [level]: item.thematicsOverviewDetails.name,
    };
    formattedData = [...formattedData, newObjToFill];
    if (Array.isArray(item[childrenKey])) {
      formatDeep(item[childrenKey], childrenKey?.toLowerCase()?.substring(0, childrenKey.length - 1), newObjToFill);
    }
  });
  return formattedData;
};

export const getPieChartData = (
  onlySelectedCategories: boolean,
  rawData: any,
  data: any,
  level: string,
  subFilterToDisplay: string | null,
  filterToDisplay: string,
  verbatimsWithoutThematicCount: number,
  t: (arg0: string) => any,
  commentsWithoutCategoriesLegendIndex: string | undefined,
  isCaptionedData: boolean,
  categoryJoinSpecify: string | null = null,
) => {
  const filteredData = (!onlySelectedCategories ? flat(rawData) : data).filter((d: any) => {
    if (level === 'thematics') {
      return d.subThematics;
    }
    if (level === 'subthematics') {
      return d.subSubThematics;
    }
    return !d.subThematics && !d.subSubThematics;
  });
  const values = filteredData.map((d: any) => {
    return subFilterToDisplay
      ? d?.thematicsOverviewDetails?.[filterToDisplay]?.[subFilterToDisplay]?.byMentionCount || 0
      : d?.thematicsOverviewDetails[filterToDisplay];
  });
  const totalValue = sumBy(values, (value: number) => value || 0) || 1;

  return [
    ...filteredData.map((d: any, ind: number) => {
      const hierarchy = {
        thematic: null,
        subThematic: null,
        subSubThematic: null,
        joinSpecify: 'and',
      };
      if (level === 'thematics') {
        hierarchy.thematic =
          isCaptionedData && d?.thematicsOverviewDetails.index
            ? d?.thematicsOverviewDetails.index
            : t(d?.thematicsOverviewDetails.name);
      } else if (level === 'subthematics') {
        hierarchy.thematic = flat(rawData)?.find(thematic =>
          thematic.subThematics?.find(
            (subThematic: any) => subThematic?.thematicsOverviewDetails.name === d?.thematicsOverviewDetails.name,
          ),
        )?.thematicsOverviewDetails.name;
        hierarchy.subThematic =
          isCaptionedData && d?.thematicsOverviewDetails.index
            ? d?.thematicsOverviewDetails.index
            : t(d?.thematicsOverviewDetails.name);
      } else {
        hierarchy.thematic = flat(rawData)?.find(thematic =>
          thematic?.subThematics?.find((subThematic: any) =>
            subThematic?.subSubThematics?.find(
              (subSubThematic: any) =>
                subSubThematic?.thematicsOverviewDetails.name === d?.thematicsOverviewDetails.name,
            ),
          ),
        )?.thematicsOverviewDetails.name;
        hierarchy.subThematic = flat(rawData)
          .filter(them => them.subSubThematics)
          ?.find(subThematic =>
            subThematic?.subSubThematics?.find(
              (subSubThematic: any) =>
                subSubThematic?.thematicsOverviewDetails?.name === d?.thematicsOverviewDetails.name,
            ),
          )?.thematicsOverviewDetails?.name;
        hierarchy.subSubThematic =
          isCaptionedData && d?.thematicsOverviewDetails.index
            ? d?.thematicsOverviewDetails.index
            : t(d?.thematicsOverviewDetails.name);
      }

      if (!categoryJoinSpecify) delete hierarchy.joinSpecify;

      return {
        originalValue: values[ind],
        value: Math.round(((100 * values[ind]) / totalValue) * 100) / 100,
        name:
          isCaptionedData && d?.thematicsOverviewDetails.index
            ? d?.thematicsOverviewDetails.index
            : t(d?.thematicsOverviewDetails.name),
        originalName: t(d?.thematicsOverviewDetails.name),
        eindex: d?.thematicsOverviewDetails.eindex,
        hierarchy,
      };
    }),
    subFilterToDisplay || verbatimsWithoutThematicCount === 0
      ? null
      : {
          originalValue: verbatimsWithoutThematicCount,
          value: Math.round(((100 * verbatimsWithoutThematicCount) / totalValue) * 100) / 100,
          name: commentsWithoutCategoriesLegendIndex
            ? t(commentsWithoutCategoriesLegendIndex)
            : t('Comments without categories'),
          originalName: t('Comments without categories'),
        },
  ].filter(d => d);
};

export const getGraphToDisplay = (
  graphToDisplay: string,
  onlySelectedCategories: boolean,
  rawData: any,
  data: any,
  level: string,
  subFilterToDisplay: string | null,
  filterToDisplay: string,
  verbatimsWithoutThematicCount: number,
  queryFilters: any,
  riverChartData: any[],
  riverChartDateType: string,
  setRiverChartDateType: {
    (value: React.SetStateAction<'day' | 'week' | 'month'>): void;
  },
  t: (arg0: string) => any,
  queryLoading: boolean,
  commentsWithoutCategoriesLegendIndex: string | undefined,
  isBenchmark: boolean,
  isCaptionedData: boolean,
  thematicsUnifiedLegend: any,
  columns: any,
  column: any,
  categoryJoinSpecify: string | null = null,
) => {
  let graph;
  switch (graphToDisplay) {
    case 'pie':
    case 'donut': {
      const chartData = getPieChartData(
        onlySelectedCategories,
        rawData,
        data,
        level,
        subFilterToDisplay,
        filterToDisplay,
        verbatimsWithoutThematicCount,
        t,
        commentsWithoutCategoriesLegendIndex,
        isCaptionedData,
        categoryJoinSpecify,
      );

      const originalValues = chartData
        ?.filter(d => t(d.name) !== t('Comments without categories'))
        ?.map(d => d.originalValue);
      graph =
        originalValues?.length && originalValues?.some(value => value !== 0) ? (
          <PieChart
            data={chartData}
            filterToDisplay={filterToDisplay}
            subFilterToDisplay={subFilterToDisplay}
            isDonut={graphToDisplay === 'donut'}
            t={t}
            queryFilters={queryFilters}
            queryLoading={queryLoading}
            isBenchmark={isBenchmark}
            thematicsUnifiedLegend={thematicsUnifiedLegend}
            level={level}
            columns={columns}
            column={column}
          />
        ) : (
          <NoDataMessage
            secondText={`${t('text:No data available for those filters')} - ${t(
              'text:Verify that the selected filters do not generate conflicts',
            )}`}
          />
        );
      break;
    }
    case 'sunburst':
    case 'treemap': {
      const getChartData = () => {
        let dataToDisplay = [
          ...(!onlySelectedCategories ? flat(rawData) : data)
            .filter((d: any) => d.subThematics)
            .map((d: any) => ({
              name: d?.thematicsOverviewDetails.name,
              value: subFilterToDisplay
                ? d?.thematicsOverviewDetails?.[filterToDisplay]?.[subFilterToDisplay]?.byMentionCount
                : d?.thematicsOverviewDetails?.[filterToDisplay],
              eindex: d?.thematicsOverviewDetails?.eindex,
              children: d.subThematics.map((st: any) => ({
                name: st?.thematicsOverviewDetails.name,
                value: subFilterToDisplay
                  ? st?.thematicsOverviewDetails?.[filterToDisplay]?.[subFilterToDisplay]?.byMentionCount
                  : st?.thematicsOverviewDetails?.[filterToDisplay],
                eindex: st?.thematicsOverviewDetails?.eindex,
                children: st?.subSubThematics.map((sst: any) => ({
                  name: sst?.thematicsOverviewDetails.name,
                  value: subFilterToDisplay
                    ? sst?.thematicsOverviewDetails?.[filterToDisplay]?.[subFilterToDisplay]?.byMentionCount
                    : sst?.thematicsOverviewDetails?.[filterToDisplay],
                  eindex: sst?.thematicsOverviewDetails.eindex,
                })),
              })),
            })),
          {
            name: t('Comments without categories'),
            value: verbatimsWithoutThematicCount,
            children: [],
          },
        ];

        const totalValue = dataToDisplay.map(d => d.value).reduce((a, b) => a + b);
        // const childrenTotalValue = dataToDisplay
        //   .map(d => d.children.map((child: any) => child.value))
        //   .reduce((a, b) => a + b);
        dataToDisplay = dataToDisplay?.map(d => ({
          ...d,
          percentage: Math.round(((100 * d.value) / totalValue) * 100) / 100 || 0,
          children: d.children.map((child: any) => ({
            ...child,
            percentage: Math.round(((100 * child.value) / totalValue) * 100) / 100 || 0,
            children: child.children.map((grandchild: any) => ({
              ...grandchild,
              percentage: Math.round(((100 * grandchild.value) / totalValue) * 100) / 100 || 0,
            })),
          })),
        }));
        return dataToDisplay;
      };
      graph = getChartData()
        ?.filter(d => d.name !== t('Comments without categories'))
        .map(d => d.value)
        .reduce((a, b) => a + b) ? (
        <TreemapChart
          data={getChartData()}
          subFilterToDisplay={subFilterToDisplay}
          filterToDisplay={filterToDisplay}
          sunburstMode={graphToDisplay === 'sunburst'}
        />
      ) : (
        <NoDataMessage
          secondText={`${t('text:No data available for those filters')} - ${t(
            'text:Verify that the selected filters do not generate conflicts',
          )}`}
        />
      );
      break;
    }
    case 'river': {
      graph = (
        <RiverChart
          subFilterToDisplay={subFilterToDisplay}
          filterToDisplay={filterToDisplay}
          level={level}
          data={riverChartData}
          riverChartDateType={riverChartDateType}
          setRiverChartDateType={setRiverChartDateType}
        />
      );
    }
  }
  return graph;
};

export const getThematicsOverviewByDatesData = ({
  thematicsOverviewByDateData,
  data,
  level,
  t,
  onlySelectedCategories,
  subFilterToDisplay,
  filterToDisplay,
  setRiverChartData,
}) => {
  let newData: any[] = [];
  const {
    getThematicsOverviewByDateData: { thematics },
  } = thematicsOverviewByDateData;
  const commentsWithoutCategoriesData = Object.entries(groupBy(thematics, t => t.date)).map(item => [
    item[0].split('-').join('/'),
    item[1].map(i => i.verbatimsWithoutThematicCount)[0],
    t('Comments without categories'),
  ]);
  let selectedThematics = [];
  if (level === 'thematics') {
    selectedThematics = thematics.filter((them: { thematicsOverviewDetails: { name: any } }) => {
      return data.find(
        (d: { thematicsOverviewDetails: { name: any } }) =>
          d?.thematicsOverviewDetails?.name === them?.thematicsOverviewDetails?.name,
      );
    });
  } else if (level === 'subthematics') {
    selectedThematics = thematics.filter((them: { subThematics: any[] }) =>
      them?.subThematics?.find((subThem: { thematicsOverviewDetails: { name: any } }) =>
        data.find(
          (d: { thematicsOverviewDetails: { name: any } }) =>
            d?.thematicsOverviewDetails?.name === subThem?.thematicsOverviewDetails?.name,
        ),
      ),
    );
  } else if (level === 'subsubthematics') {
    selectedThematics = thematics.filter((them: { subThematics: any[] }) =>
      them?.subThematics?.find((subThem: { subSubThematics: any[] }) =>
        subThem.subSubThematics?.find((subSubThem: { thematicsOverviewDetails: { name: any } }) =>
          data.find(
            (d: { thematicsOverviewDetails: { name: any } }) =>
              d?.thematicsOverviewDetails?.name === subSubThem?.thematicsOverviewDetails?.name,
          ),
        ),
      ),
    );
  }

  newData = [...newData, ...commentsWithoutCategoriesData];
  if (level === 'thematics') {
    if (
      uniq(
        (!onlySelectedCategories ? thematics : selectedThematics).map(
          (d: { thematicsOverviewDetails: { name: any } }) => d?.thematicsOverviewDetails?.name,
        ),
      ).filter(them =>
        data.find((d: { thematicsOverviewDetails: { name: unknown } }) => d?.thematicsOverviewDetails.name === them),
      ).length <= 20
    ) {
      (!onlySelectedCategories ? thematics : selectedThematics).forEach((thematic: any) => {
        newData = [
          ...newData,
          [
            thematic.date.split('-').join('/'),
            subFilterToDisplay
              ? thematic?.thematicsOverviewDetails?.[filterToDisplay]?.[subFilterToDisplay]?.byMentionCount || 0
              : thematic?.thematicsOverviewDetails[filterToDisplay],
            thematic?.thematicsOverviewDetails.name,
          ],
        ];
      });
      const totalValue = newData?.map(d => d[1]).length && newData?.map(d => d[1])?.reduce((a, b) => a + b);
      newData = newData.map(d => [...d, Math.round(((100 * d[1]) / totalValue) * 100) / 100]);
    } else {
      newData = ['Too many categories'];
    }
  } else if (level === 'subthematics') {
    if (
      uniq(
        flatten(
          (!onlySelectedCategories ? thematics : selectedThematics).map(
            (thematic: { subThematics: any }) => thematic.subThematics,
          ),
        ).map(d => d?.thematicsOverviewDetails?.name),
      ).filter(them =>
        data.find((d: { thematicsOverviewDetails: { name: any } }) => d?.thematicsOverviewDetails.name === them),
      )?.length <= 20
    ) {
      (!onlySelectedCategories ? thematics : selectedThematics)?.forEach((thematic: any) => {
        thematic.subThematics
          .filter((subThem: { thematicsOverviewDetails: { name: any } }) =>
            data.find(
              (d: { thematicsOverviewDetails: { name: any } }) =>
                d?.thematicsOverviewDetails?.name === subThem?.thematicsOverviewDetails?.name,
            ),
          )
          ?.forEach((subThematic: any) => {
            newData = [
              ...newData,
              [
                thematic.date.split('-').join('/'),
                subFilterToDisplay
                  ? subThematic?.thematicsOverviewDetails?.[filterToDisplay]?.[subFilterToDisplay]?.byMentionCount || 0
                  : subThematic?.thematicsOverviewDetails[filterToDisplay],
                subThematic?.thematicsOverviewDetails.name,
              ],
            ];
          });
      });
      const totalValue = newData.map(d => d[1]).reduce((a, b) => a + b, 0);
      newData = newData.map(d => [...d, Math.round(((100 * d[1]) / totalValue) * 100) / 100]);
    } else {
      newData = ['Too many categories'];
    }
  } else if (level === 'subsubthematics') {
    if (
      uniq(
        flatten(
          flatten(
            (!onlySelectedCategories ? thematics : selectedThematics).map(
              (thematic: { subThematics: any }) => thematic.subThematics,
            ),
          ).map(d => d.subSubThematics),
        ).map(d => d?.thematicsOverviewDetails?.name),
      ).filter(them =>
        data.find((d: { thematicsOverviewDetails: { name: any } }) => d?.thematicsOverviewDetails.name === them),
      )?.length <= 20
    ) {
      (!onlySelectedCategories ? thematics : selectedThematics)?.forEach((thematic: any) => {
        thematic.subThematics?.forEach((subThematic: any) => {
          subThematic.subSubThematics
            ?.filter((subSubThem: { thematicsOverviewDetails: { name: any } }) =>
              data.find(
                (d: { thematicsOverviewDetails: { name: any } }) =>
                  d?.thematicsOverviewDetails?.name === subSubThem?.thematicsOverviewDetails?.name,
              ),
            )
            ?.forEach((subSubThematic: any) => {
              newData = [
                ...newData,
                [
                  thematic.date.split('-').join('/'),
                  subFilterToDisplay
                    ? subSubThematic?.thematicsOverviewDetails?.[filterToDisplay]?.[subFilterToDisplay]
                        ?.byMentionCount || 0
                    : subSubThematic?.thematicsOverviewDetails[filterToDisplay],
                  subSubThematic?.thematicsOverviewDetails.name,
                ],
              ];
              const totalValue = newData.map(d => d[1]).reduce((a, b) => a + b);
              newData = newData.map(d => [...d, Math.round(((100 * d[1]) / totalValue) * 100) / 100]);
            });
        });
      });
    } else {
      newData = ['Too many categories'];
    }
  }
  const commentsWithoutCategories = newData.shift();
  newData = [...newData, commentsWithoutCategories];
  setRiverChartData(newData);
};

export const getSelectedThematicsForSettings = (
  selectionVar: Array<{ thematic: string; subThematic: string | undefined; subSubThematic: string | undefined }>,
) => {
  return selectionVar?.map(thematic => {
    return {
      thematic: thematic?.thematic,
      subthematic: thematic?.subThematic,
      subsubthematic: thematic?.subSubThematic,
    };
  });
};

export const getDefaultSettings = (categoriesHierarchies: any) => {
  return {
    selectedThematics: categoriesHierarchies,
    categoryRanking: {
      selectedColumns: [
        'mentionCount',
        'verbatimPercentage',
        'eindex',

        'sentiments.positive.impactSat',
        'sentiments.negative.impactSat',

        'emotionalIntensity',
        'speechEngagement',

        'mentionEvolution.yearBefore',
        // 'mentionEvolution.periodBefore',

        'eindexEvolution.yearBefore',
        // 'eindexEvolution.periodBefore',

        // 'sentiments.positive.byPercentage',
        // 'sentiments.positive.byMentionCount',
        // 'sentiments.neutral.byPercentage',
        // 'sentiments.neutral.byMentionCount',
        // 'sentiments.negative.byPercentage',
        // 'sentiments.negative.byMentionCount',

        // 'sentimentsEvolution.positive.yearBefore',
        // 'sentimentsEvolution.positive.periodBefore',
        // 'sentimentsEvolution.neutral.yearBefore',
        // 'sentimentsEvolution.neutral.periodBefore',
        // 'sentimentsEvolution.negative.yearBefore',
        // 'sentimentsEvolution.negative.periodBefore',

        // 'emotions.happiness.byPercentage',
        // 'emotions.happiness.byMentionCount',

        // 'emotions.surprise.byPercentage',
        // 'emotions.surprise.byMentionCount',

        // 'emotions.calm.byPercentage',
        // 'emotions.calm.byMentionCount',

        // 'emotions.fear.byPercentage',
        // 'emotions.fear.byMentionCount',

        // 'emotions.sadness.byPercentage',
        // 'emotions.sadness.byMentionCount',

        // 'emotions.anger.byPercentage',
        // 'emotions.anger.byMentionCount',

        // 'emotions.disgust.byPercentage',
        // 'emotions.disgust.byMentionCount',

        // 'emotionsEvolution.happiness.yearBefore',
        // 'emotionsEvolution.happiness.periodBefore',

        // 'emotionsEvolution.surprise.yearBefore',
        // 'emotionsEvolution.surprise.periodBefore',

        // 'emotionsEvolution.calm.yearBefore',
        // 'emotionsEvolution.calm.periodBefore',

        // 'emotionsEvolution.sadness.yearBefore',
        // 'emotionsEvolution.sadness.periodBefore',

        // 'emotionsEvolution.fear.yearBefore',
        // 'emotionsEvolution.fear.periodBefore',

        // 'emotionsEvolution.anger.yearBefore',
        // 'emotionsEvolution.anger.periodBefore',

        // 'emotionsEvolution.disgust.yearBefore',
        // 'emotionsEvolution.disgust.periodBefore',
      ],
      sortedBy: null,
    },
    emotionsTriggers: {
      selectedIndicator: ['happiness'],
      datazoom: {
        start: 0,
        end: 100,
      },
      display: ['thematics', 'subThematics', 'subSubThematics'],
    },
    dataByThematics: {
      selectedFilter: 'thematics',
      indicators: {
        badge: {
          label: 'E-index',
          id: 'eindex',
        },
        bar: {
          label: 'Mentions',
          id: 'mentions',
          color: '#592ea0',
          selected: [],
        },
      },
      orderBy: {
        label: 'Hierarchical - Ascending',
        id: 'hierarchy_asc',
      },
    },
    horizontalDataByThematics: {
      selectedFilter: 'thematics',
      indicators: {
        badge: {
          label: 'E-index',
          id: 'eindex',
        },
        bar: {
          label: 'Emotions volume',
          id: 'emotions.volume',
        },
      },
      orderBy: {
        label: 'Mentions - Ascending',
        id: 'mentionCount_asc',
      },
    },
    categoryBreakdown: {
      graphToDisplay: 'pie',
      levelToDisplay: 'thematics',
      onlySelectedCategories: true,
      riverChartDateType: 'week',
    },
  };
};
